import type React from "react";
import styled from "styled-components";
import { Typography } from "@bracket/ui/components";
import { getTheme } from "@bracket/ui/themes";

export type NotificationType = "success" | "error" | "info";

export type Notification = {
	id: string;
	title: string;
	message: string;
	type: "success" | "error" | "info";
	primaryAction?: {
		label: string;
		onClick: () => void;
	};
	secondaryAction?: {
		label: string;
		onClick: () => void;
	};
	status: string;
};

export type NotificationProps = Notification & {
	onClose: (id: string) => void;
};

const NotificationWrapper = styled.div<{ $type: NotificationType }>`
	position: relative;
	color: ${() => getTheme().surface_on};
	border-radius: 8px;
	margin-bottom: 0.5rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
	min-width: 300px;

	border: 1px solid ${({ $type }) => {
		const theme = getTheme();
		// Extract RGB from hex (handle 8-digit hex with alpha)
		const color = $type === "error" ? theme.error : theme.success;
		const hex = color.length === 9 ? color.slice(0, 7) : color; // Remove alpha if present
		return hex;
	}};

	padding: 1rem;
	background-color: ${() => {
		const theme = getTheme();
		// Use theme background color with 90% opacity
		// Dark theme: black background, Light theme: white background
		const bgColor = theme.palette_type === "dark" ? theme.black : theme.white;
		// Convert hex to rgba with 0.90 opacity
		const r = parseInt(bgColor.slice(1, 3), 16);
		const g = parseInt(bgColor.slice(3, 5), 16);
		const b = parseInt(bgColor.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, 0.90)`;
	}};

	@media (min-width: 1024px) {
		padding: 1.5rem;
	}
`;

const ActionsWrapper = styled.div`
	display: flex;
	gap: 8px;
`;

const Button = styled.button<{ $isPrimary?: boolean; $type: NotificationType }>`
	background-color: ${({ $isPrimary, $type }) => {
		if($isPrimary) {
			const theme = getTheme();
			const color = $type === "error" ? theme.error : theme.success;
			// Extract RGB from hex (handle 8-digit hex with alpha)
			const hex = color.length === 9 ? color.slice(0, 7) : color;
			return hex;
		}
		return "transparent";
	}};
	color: ${({ $isPrimary, $type }) => {
		const theme = getTheme();
		if($isPrimary) {
			return theme.palette_type === "dark" ? theme.black : theme.white;
		}
		const color = $type === "error" ? theme.error : theme.success;
		// Extract RGB from hex (handle 8-digit hex with alpha)
		const hex = color.length === 9 ? color.slice(0, 7) : color;
		return hex;
	}};
	border: ${({ $isPrimary, $type }) => {
		if($isPrimary) {
			return "none";
		}
		const theme = getTheme();
		const color = $type === "error" ? theme.error : theme.success;
		// Extract RGB from hex (handle 8-digit hex with alpha)
		const hex = color.length === 9 ? color.slice(0, 7) : color;
		return `1px solid ${hex}`;
	}};
	border-radius: 4px;
	padding: 8px 16px;
	cursor: pointer;
	font-size: 14px;
	font-weight: 500;
	transition: background-color 0.2s, color 0.2s;

	&:hover {
		background-color: ${({ $isPrimary, $type }) => {
			if($isPrimary) {
				const theme = getTheme();
				const color = $type === "error" ? theme.error : theme.success;
				// Extract RGB from hex (handle 8-digit hex with alpha)
				const hex = color.length === 9 ? color.slice(0, 7) : color;
				return hex;
			}
			const theme = getTheme();
			const color = $type === "error" ? theme.error : theme.success;
			// Extract RGB from hex (handle 8-digit hex with alpha)
			const hex = color.length === 9 ? color.slice(0, 7) : color;
			const r = parseInt(hex.slice(1, 3), 16);
			const g = parseInt(hex.slice(3, 5), 16);
			const b = parseInt(hex.slice(5, 7), 16);
			return `rgba(${r}, ${g}, ${b}, 0.2)`;
		}};
	}
`;

export const Notification: React.FC<NotificationProps> = ({
	id,
	title,
	message,
	type,
	primaryAction,
	secondaryAction,
	onClose,
}) => {
	return (
		<NotificationWrapper $type={type}>
			<Typography
				variant="textLG"
				color={type === "success" ? getTheme().success_on : type === "error" ? getTheme().error_on : getTheme().success_on}
			>
				{title}
			</Typography>
			<Typography variant="p" color={getTheme().paragraph}>
				{message}
			</Typography>
			<ActionsWrapper>
				{primaryAction && (
					<Button
						$isPrimary
						$type={type}
						onClick={() => {
							primaryAction.onClick();
							onClose(id);
						}}
					>
						{primaryAction.label}
					</Button>
				)}
				{secondaryAction && (
					<Button
						$type={type}
						onClick={() => {
							secondaryAction.onClick();
							onClose(id);
						}}
					>
						{secondaryAction.label}
					</Button>
				)}
			</ActionsWrapper>
		</NotificationWrapper>
	);
};

const NotificationContainer = styled.div`
	position: fixed;
	z-index: 1000000;
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	top: 0;
	right: 0;
	margin: 10px;

	@media (min-width: 1024px) {
		top: 20px;
		right: 20px;
		marign: 0;
	}
`;

export type NotificationManagerProps = {
	notifications: NotificationProps[];
	onClose: (id: string) => void;
};

export const NotificationManager: React.FC<NotificationManagerProps> = ({
	notifications,
	onClose,
}) => {
	return (
		<NotificationContainer>
			{notifications.map((notification) => (
				<Notification
					key={notification.id}
					{...notification}
					onClose={onClose}
				/>
			))}
		</NotificationContainer>
	);
};
